import { useState, useEffect, useRef } from "react";
import { Card, Avatar, Tag, Empty, Spin, Segmented, Typography } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../hooks";
import { PatientReviApi } from "../../../../api/revi/patientApi";
import { useEvaluationChartsController } from "../../../../hooks/revi/useEvaluationChartsController";
import {
  EvaluationPicker,
  RadarProgressChart,
  ProgressionLineChart,
  SessionsBarChart,
} from "../EvaluationChartsCmp";
// EL MISMO COMPONENTE QUE USA LA FICHA DEL PACIENTE, no una copia.
//
// El dashboard no ensenaba las terapias VR: esta seccion solo hablaba con
// `/evaluation-summary`, que lee resultados de tests, y no sabia que existen
// las sesiones de telemetria. Habia que ir a "Ver usuarios" y abrir la ficha
// para verlas.
//
// Se reutiliza tal cual en vez de extraer una pieza comun porque el componente
// ya era autonomo: recibe `patientId` y `active` y no sabe nada del modal. Asi
// la paridad entre las dos pantallas no es algo que haya que mantener, es que
// literalmente son el mismo codigo.
import { TherapistPatientMotricityTab } from "../PatientCmp/TherapistPatientMotricityTab";
import "./ClinicalProgressionSection.scss";

const { Text } = Typography;
const patientApi = new PatientReviApi();

function calcAge(birthdate) {
  if (!birthdate) return null;
  const d = new Date(birthdate);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  return age;
}

// Recibe `t` por parametro: es una funcion auxiliar, no un componente, y los
// hooks solo pueden vivir dentro del render.
function sexoLabel(sexo, t) {
  if (sexo === "M") return t("clinicalProgression.sex.male");
  if (sexo === "F") return t("clinicalProgression.sex.female");
  if (sexo === "O") return t("clinicalProgression.sex.other");
  return null;
}

export function ClinicalProgressionSection() {
  // Dos namespaces: el propio del area y el comun, del que sale "Paciente".
  const { t } = useTranslation(["motricity", "common"]);
  const { accessToken } = useAuth();

  // --- Patient list ---
  const [patients, setPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingPatients(true);
        const res = await patientApi.getMyForTherapist(accessToken, {
          limit: 200,
        });
        if (!cancelled) setPatients(res.patients || []);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoadingPatients(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  // --- Charts hook — use the hook's own selectedPatientId ---
  const {
    selectedPatientId,
    setSelectedPatientId,
    evaluationSummary,
    selectedEvaluationId,
    setSelectedEvaluationId,
    timeRange,
    setTimeRange,
    progression,
    latestResult,
    loading: chartsLoading,
    isEmpty,
  } = useEvaluationChartsController({ patientId: null });

  // Track whether patient has evaluations in any timeRange
  const everHadData = useRef(false);
  const prevPatientId = useRef(selectedPatientId);
  if (prevPatientId.current !== selectedPatientId) {
    everHadData.current = false;
    prevPatientId.current = selectedPatientId;
  }
  if (!isEmpty && !chartsLoading) {
    everHadData.current = true;
  }
  const showCharts = selectedPatientId && everHadData.current;

  // Que mundo se esta mirando. NO se reinicia al cambiar de paciente a
  // proposito: quien esta comparando terapias VR entre pacientes quiere seguir
  // en terapias VR al pinchar el siguiente, no volver a tests cada vez.
  const [vista, setVista] = useState("evaluaciones");

  const handlePatientClick = (id) => {
    setSelectedPatientId(id === selectedPatientId ? null : id);
  };

  return (
    <Card
      title={t("clinicalProgression.title")}
      variant="outlined"
      className="revi-clinical-progression"
    >
      {loadingPatients ? (
        <div className="revi-clinical-progression__center">
          <Spin />
        </div>
      ) : patients.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("clinicalProgression.noPatients")}
        />
      ) : (
        <div className="revi-clinical-progression__patients">
          {patients.map((p) => {
            const isSelected = selectedPatientId === p._id;
            // El nombre viene de la BD y no se traduce; solo el texto de
            // reserva cuando el paciente no tiene nombre registrado.
            const fullName =
              `${p.name || ""} ${p.surname || p.lastname || ""}`.trim() ||
              t("common:roles.patient");
            const age = calcAge(p.birthdate);
            const sexo = sexoLabel(p.sexo, t);
            return (
              <div
                key={p._id}
                className={`revi-clinical-progression__patient-chip${isSelected ? " revi-clinical-progression__patient-chip--active" : ""}`}
                onClick={() => handlePatientClick(p._id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    handlePatientClick(p._id);
                }}
              >
                <Avatar size={36} src={p.avatar} icon={<UserOutlined />} />
                <div className="revi-clinical-progression__patient-info">
                  <Text className="revi-clinical-progression__patient-name">
                    {fullName}
                  </Text>
                  <div className="revi-clinical-progression__patient-tags">
                    {sexo && <Tag>{sexo}</Tag>}
                    {/* Contador: en frances el cero y el uno van en singular. */}
                    {age !== null && (
                      <Tag>{t("common:contadores.anios", { count: age })}</Tag>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedPatientId ? (
        <>
          {/* Los dos mundos de REVI, separados. No se mezclan en una sola
              lista porque no son comparables: un test lo contesta una persona
              y se mide con un baremo publicado; una terapia VR la emite la
              experiencia y su baremo es propio. Ponerlos juntos invitaria a
              leerlos con la misma vara. */}
          <Segmented
            className="revi-clinical-progression__vista"
            value={vista}
            onChange={setVista}
            options={[
              {
                label: t("clinicalProgression.vistas.evaluaciones"),
                value: "evaluaciones",
              },
              {
                label: t("clinicalProgression.vistas.terapiasVr"),
                value: "vr",
              },
            ]}
          />

          {vista === "vr" ? (
            <TherapistPatientMotricityTab
              patientId={selectedPatientId}
              active
            />
          ) : !showCharts && isEmpty ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("clinicalProgression.noEvaluations")}
            />
          ) : showCharts ? (
            <>
              <EvaluationPicker
                evaluationSummary={evaluationSummary}
                selectedEvaluationId={selectedEvaluationId}
                onSelect={setSelectedEvaluationId}
                timeRange={timeRange}
                onTimeRangeChange={setTimeRange}
                loading={chartsLoading}
              />
              {selectedEvaluationId ? (
                <div className="revi-clinical-progression__charts-grid">
                  <RadarProgressChart
                    loading={chartsLoading}
                    dimensions={latestResult?.processedScore?.dimensions ?? []}
                  />
                  <ProgressionLineChart
                    loading={chartsLoading}
                    history={progression.history}
                  />
                  <SessionsBarChart
                    loading={chartsLoading}
                    history={progression.history}
                  />
                </div>
              ) : evaluationSummary.length > 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t("clinicalProgression.selectTest")}
                  style={{ padding: "24px 0" }}
                />
              ) : null}
            </>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}
